import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toOptions } from '@/shared/lib/options';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { fetchCities, fetchCountries, fetchRegions } from '../users/api';
import {
  EMPTY_PERIOD,
  editContacts,
  fetchContacts,
  type ContactsData,
  type ContactsFormValues,
  type ScheduleKey,
} from './api';
import { ReviewPanel } from './ReviewPanel';
import { ScheduleField } from './ScheduleField';
import { SectionLayout } from './SectionLayout';
import { getSectionStatus, useRequestId } from './sectionHelpers';
import { useCanEditVerification } from './useCanEditVerification';

type TextField = 'name' | 'phone' | 'email' | 'address' | 'lat' | 'long';

const TEXT_FIELDS: { field: TextField; label: string }[] = [
  { field: 'name', label: 'Название' },
  { field: 'phone', label: 'Телефон' },
  { field: 'email', label: 'E-mail' },
  { field: 'address', label: 'Адрес' },
  { field: 'lat', label: 'Широта' },
  { field: 'long', label: 'Долгота' },
];

const SCHEDULE_FIELDS: { key: ScheduleKey; label: string }[] = [
  { key: 'weekdays', label: 'Рабочие дни' },
  { key: 'weekends', label: 'Выходные' },
  { key: 'holidays', label: 'Праздничные' },
];

const toFormValues = (data: ContactsData): ContactsFormValues => ({
  name: data.name ?? '',
  phone: data.phone ?? '',
  email: data.email ?? '',
  description: data.description ?? '',
  address: data.address ?? '',
  countryId: String(data.country_id ?? ''),
  regionId: String(data.region_id ?? ''),
  cityId: String(data.city_id ?? ''),
  lat: String(data.lat ?? ''),
  long: String(data.long ?? ''),
  schedule: {
    weekdays: data.schedule?.weekdays ?? EMPTY_PERIOD,
    weekends: data.schedule?.weekends ?? EMPTY_PERIOD,
    holidays: data.schedule?.holidays ?? EMPTY_PERIOD,
  },
});

const ContactsForm = ({ requestId, data }: { requestId: number; data: ContactsData }) => {
  const qc = useQueryClient();
  const canEdit = useCanEditVerification();
  const [values, setValues] = useState(() => toFormValues(data));
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const countries = useQuery({ queryKey: ['countries'], queryFn: fetchCountries });
  const regions = useQuery({
    queryKey: ['regions', values.countryId],
    queryFn: () => fetchRegions(values.countryId),
    enabled: Boolean(values.countryId),
  });
  const cities = useQuery({
    queryKey: ['cities', values.regionId],
    queryFn: () => fetchCities(values.regionId),
    enabled: Boolean(values.regionId),
  });

  const { errors, clearErrors, save, isPending } = useSaveForm(
    (form: ContactsFormValues) => editContacts(requestId, form),
    () => {
      setDirty(false);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['verification', 'contacts', requestId] });
    },
  );

  const patch = (change: Partial<ContactsFormValues>, ...touched: string[]) => {
    setValues((prev) => ({ ...prev, ...change }));
    setDirty(true);
    setSaved(false);
    clearErrors(...touched);
  };

  const hasCoords = Boolean(values.lat && values.long);
  const commonErrors = [...(errors.contacts ?? []), ...(errors.common ?? [])];

  return (
    <>
      {TEXT_FIELDS.map(({ field, label }) => (
        <FormField key={field} label={label} errors={errors[field] ?? (field === 'lat' ? errors.long : undefined)}>
          <input
            className="form-control input-sm"
            style={{ maxWidth: 500 }}
            value={values[field]}
            readOnly={!canEdit}
            onChange={(e) => patch({ [field]: e.target.value }, field)}
          />
        </FormField>
      ))}
      {hasCoords && (
        <p>
          <a
            href={`https://yandex.ru/maps/?pt=${values.long},${values.lat}&z=16&l=map`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Показать на карте
          </a>
        </p>
      )}
      <FormField label="Описание" errors={errors.description}>
        <textarea
          className="form-control"
          rows={5}
          style={{ maxWidth: 500 }}
          value={values.description}
          readOnly={!canEdit}
          onChange={(e) => patch({ description: e.target.value }, 'description')}
        />
      </FormField>
      <FormField label="Страна" errors={errors.country_id}>
        <FilterSelect
          options={toOptions(countries.data)}
          value={values.countryId}
          isLoading={countries.isLoading}
          isDisabled={!canEdit}
          isClearable={false}
          placeholder="Страна"
          onChange={(countryId) => patch({ countryId, regionId: '', cityId: '' }, 'country_id')}
        />
      </FormField>
      <FormField label="Регион" errors={errors.region_id}>
        <FilterSelect
          options={toOptions(regions.data)}
          value={values.regionId}
          isLoading={regions.isFetching}
          isDisabled={!canEdit || !values.countryId}
          isClearable={false}
          placeholder="Регион"
          onChange={(regionId) => patch({ regionId, cityId: '' }, 'region_id')}
        />
      </FormField>
      <FormField label="Город" errors={errors.city_id}>
        <FilterSelect
          options={toOptions(cities.data)}
          value={values.cityId}
          isLoading={cities.isFetching}
          isDisabled={!canEdit || !values.regionId}
          isClearable={false}
          placeholder="Город"
          onChange={(cityId) => patch({ cityId }, 'city_id')}
        />
      </FormField>
      <h3 style={{ fontSize: 18 }}>График работы</h3>
      {SCHEDULE_FIELDS.map(({ key, label }) => (
        <ScheduleField
          key={key}
          label={label}
          value={values.schedule[key]}
          disabled={!canEdit}
          onChange={(period) => patch({ schedule: { ...values.schedule, [key]: period } })}
        />
      ))}
      <ReviewPanel
        requestId={requestId}
        section="contacts"
        status={getSectionStatus(data.status)}
        initialRejectMessage={data.reject_message}
        disableVerify={dirty}
        feedback={
          <>
            {commonErrors.map((message) => (
              <p key={message} className="text-red" style={{ marginTop: 10 }}>
                {message}
              </p>
            ))}
            {saved && <p className="text-green">Успешно</p>}
          </>
        }
        actions={
          dirty && canEdit ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ marginRight: 12 }}
              disabled={isPending}
              onClick={() => save(values)}
            >
              Сохранить
            </button>
          ) : null
        }
      />
    </>
  );
};

export const VerificationContactsPage = () => {
  const requestId = useRequestId();
  const { data, isLoading, error } = useQuery({
    queryKey: ['verification', 'contacts', requestId],
    queryFn: () => fetchContacts(requestId),
  });

  return (
    <SectionLayout
      title="Контактная информация"
      status={getSectionStatus(data?.status)}
      isLoading={isLoading}
      error={error}
    >
      {data && <ContactsForm key={requestId} requestId={requestId} data={data} />}
    </SectionLayout>
  );
};
